import { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.svg";
import AdminService from "../../services/admin.services";
import FoodLoader from "src/components/Loader";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const initialState = {
  email: "",
  password: "",
};

function Login() {
  const [formData, setFormData] = useState(initialState);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const inputStyle =
    "w-full px-[20px] py-[12px] border border-black text-[14px] rounded-[8px]";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { email, password } = formData;

    if (!email || !password) {
      setIsLoading(false);
      setModalMessage("Please provide both email and password.");
      setShowErrorModal(true);
      return;
    }

    try {
      const adminService = new AdminService();
      const response = await adminService.login(email, password);
      setIsLoading(false);

      if (response && response.token) {
        setModalMessage("Login successful! Redirecting...");
        setShowSuccessModal(true);
        setTimeout(() => navigate("/pr-admin/dashboard"), 2000);
      } else {
        setModalMessage("Login failed. Please check your credentials.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsLoading(false);
      setModalMessage(
        `${error instanceof Error ? error.message : String(error)}`
      );
      setShowErrorModal(true);
    }
  };

  const Modal = ({
    message,
    isSuccess,
    onClose,
  }: {
    message: string;
    isSuccess: boolean;
    onClose: () => void;
  }) => (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className={`bg-${
          isSuccess ? "green" : "red"
        }-500 text-white p-4 rounded-lg shadow-lg`}
      >
        <p>{message}</p>
        <button
          className="mt-2 bg-white text-black px-4 py-2 rounded"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-[100vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Logging in"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  }

  return (
    <div className="h-[100vh] w-full bg-white">
      <img
        src={logo}
        alt="logo"
        className="mx-auto lg:w-[360px] md:w-[200px] mb-[3.5rem] mt-[105px]"
      />
      <div className="lg:w-[420px] md:w-[70%] sm:w-[70%] mx-auto px-5 md:px-0">
        <h2 className="text-center md:text-[27px] text-[23px] font-bold leading-[48px] mb-[2.5rem]">
          ADMIN LOGIN
        </h2>
        <form onSubmit={handleSubmit} className="grid gap-y-7">
          <input
            placeholder="Email"
            type="email"
            className={`${inputStyle}`}
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />
          <div className="relative">
            <input
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              className={`${inputStyle} pr-10`}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
            <span
              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-600"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label="Toggle password visibility"
              role="button"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full text-black font-normal bg-pr-primary rounded-[8px] px-[20px] py-[12px] text-[18px] mt-[3rem] disabled:opacity-50"
          >
            Login
          </button>
        </form>
      </div>
      {showSuccessModal && (
        <Modal
          message={modalMessage}
          isSuccess={true}
          onClose={() => setShowSuccessModal(false)}
        />
      )}
      {showErrorModal && (
        <Modal
          message={modalMessage}
          isSuccess={false}
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  );
}

export default Login;
